/**
 * Named T&C templates that drive the customer-facing terms section of a
 * Check-In session. Stored as rows in the `cr001_check_in_terms_template`
 * Dataverse entity; admins manage them through the Check-In Terms admin
 * page.
 *
 * Modelled on {@link QuoteTermsTemplate} (see `quoteTermsTemplate.ts`),
 * with two deliberate divergences driven by legal immutability:
 *
 *   1. A `version` (`cr001_version`) — Quote Terms has none. It is the
 *      legal identifier recorded on the customer's acceptance snapshot
 *      (`CheckInTermsAcceptance.termsVersion`). Bump it when the terms
 *      change materially. Quote terms resolve *live* and never snapshot;
 *      check-in **freezes** the accepted body + version onto the session
 *      (`check_in_terms_service.accept_terms`) and the server — not the
 *      client — is authoritative about which version was accepted.
 *
 *   2. An `intakeType` scope (`cr001_intake_type`) — in-store and remote
 *      customers get **different** terms. Each intake type carries its own
 *      default; the public intake surface resolves the terms for the
 *      session's own intake type.
 *
 * Templating arrived after the foundation: bodies support the same
 * `{{path}}` + `{{#if path}}` grammar quote terms use, against the registry in
 * {@link CHECKIN_TEMPLATE_VARIABLES}. The divergence that matters is *where* it
 * resolves. Quote terms render in the browser, because nothing is recorded.
 * Check-in terms resolve in `sm4rt-public-api` — the server is authoritative
 * about the body it freezes onto the acceptance, so it cannot take the page's
 * word for the resolved text. The browser renders the same values only to
 * display them.
 *
 * Audit fields (`createdAt` / `updatedAt` / `updatedBy`) are populated from
 * Dataverse's built-in `createdon` / `modifiedon` / `modifiedby` columns;
 * the service never sets them directly.
 */

/**
 * Intake types that carry a customer-facing terms surface. A narrowed view
 * of `CheckInIntakeType` (`checkIn.ts`) — only the two public surfaces
 * (`in_store` QR/kiosk, `online` remote link) present terms to a customer.
 */
import type { TermsBodyFormat } from './termsFormat';

export type CheckInTermsIntakeType = 'in_store' | 'online';

export interface CheckInTermsTemplate {
  /** Dataverse GUID (`cr001_check_in_terms_templateid`). Server-managed. */
  id: string;
  /**
   * Stable string identifier. Immutable after creation; survives template
   * rebuilds / soft delete. Slug-shaped (`^[a-z0-9][a-z0-9_-]{0,62}$`).
   */
  slug: string;
  /** Display label shown in the admin list. */
  name: string;
  /** Optional admin memo — never rendered to the customer. */
  description?: string;
  /** Template body — the terms text shown to the customer (static prose). */
  body: string;
  /** Optional friendly closer rendered as a separate paragraph below the body. */
  closer?: string;
  /**
   * How `body` and `closer` render — see {@link TermsBodyFormat}. Null in
   * Dataverse (`cr001_body_format`) on rows written before markdown support,
   * and read as `'text'`.
   *
   * The format is snapshotted with the acceptance, not just the body: a record
   * of what was agreed has to know how the text was displayed, or re-rendering
   * it years later can produce different structure from what the customer read.
   */
  bodyFormat: TermsBodyFormat;
  /**
   * The intake surface these terms apply to. Exactly one template per
   * `intakeType` is the default; the invariant is enforced server-side.
   */
  intakeType: CheckInTermsIntakeType;
  /**
   * Version identifier snapshotted onto a customer's acceptance
   * (e.g. `"2026-08-15"` or `"v3"`). The server records this at accept
   * time; a stale echo from the client is rejected.
   */
  version: string;
  /** Exactly one template per `intakeType` has this true; enforced server-side. */
  isDefault: boolean;
  /** Dataverse `createdon` (ISO timestamp). */
  createdAt: string;
  /** Dataverse `modifiedon` (ISO timestamp). */
  updatedAt: string;
  /** Dataverse `modifiedby` UPN; null when not resolvable. */
  updatedBy: string | null;
}

/** Body for POST `/api/check-in-terms-templates`. */
export interface CheckInTermsTemplateCreate {
  slug: string;
  name: string;
  intakeType: CheckInTermsIntakeType;
  version: string;
  body: string;
  description?: string;
  closer?: string;
  /** Omitted defaults to `'markdown'`. */
  bodyFormat?: TermsBodyFormat;
  isDefault?: boolean;
}

/** Body for PATCH `/api/check-in-terms-templates/{id}`. All fields optional. */
export interface CheckInTermsTemplateUpdate {
  name?: string;
  description?: string;
  body?: string;
  closer?: string;
  bodyFormat?: TermsBodyFormat;
  version?: string;
  isDefault?: boolean;
}

export interface CheckInTermsTemplatesListResponse {
  items: CheckInTermsTemplate[];
}

/**
 * Single template variable usable inside a check-in terms body.
 *
 * Deliberately its own type rather than a reuse of {@link QuoteTemplateVariable}:
 * the sources differ (a check-in has a session, not a quote), and it carries a
 * `fallback`, which quote terms have no need for.
 */
export interface CheckInTemplateVariable {
  /** Dot-path referenced inside `{{ ... }}`. */
  path: string;
  /** Human-friendly label rendered in the editor help panel. */
  label: string;
  /** Realistic example of the formatted output (for the help panel). */
  example: string;
  /** Where the value originates — drives grouping in the help panel. */
  source: 'appSettings' | 'session' | 'customer' | 'system';
  /**
   * What renders when the value is not yet known.
   *
   * This exists because of the order of the intake flow: the terms are shown
   * on step one and contact details are captured on step two, so a kiosk
   * customer reads the terms before the session knows their name. Rendering
   * `{{customer.name}}` as nothing would leave a hole mid-sentence, so it
   * renders "you" instead.
   *
   * The fallback is part of the contract, not a display nicety: the public API
   * resolves these server-side and freezes the result onto the acceptance, so
   * both sides must substitute the same word. Empty string where a variable
   * reads better absent — `{{#if customer.businessName}}` then behaves as an
   * author expects, whereas a defaulted `customer.name` is always truthy.
   */
  fallback?: string;
}

/**
 * Canonical registry of variables exposed to check-in terms bodies.
 *
 * **Bounded by the Dataverse grant, not by taste.** `sm4rt-public-api` holds a
 * deliberately narrow role (its background.md §6.2): the session row, the draft
 * jobs' charge total, the terms template, and one key out of the app-settings
 * row. Every variable here comes from one of those. A variable that would need
 * another table is not a variable this app can offer — it is a signal the value
 * belongs in the trusted zone.
 *
 * To add one: add an entry here, implement the resolver in BOTH
 * `sm4rt-web/web/src/utils/checkInTemplateRenderer.ts` and
 * `sm4rt-public-api/core/services/template_variables.py`, and extend
 * `fixtures/terms-template-rendering.json` so the two stay honest.
 */
export const CHECKIN_TEMPLATE_VARIABLES: readonly CheckInTemplateVariable[] = [
  // appSettings — the same values quote terms expose, from the same row.
  {
    path: 'businessHoursStart',
    label: 'Business hours start',
    example: '9:00 AM',
    source: 'appSettings',
  },
  {
    path: 'businessHoursEnd',
    label: 'Business hours end',
    example: '5:00 PM',
    source: 'appSettings',
  },
  {
    path: 'shoulderHoursStart',
    label: 'Shoulder hours start',
    example: '7:00 AM',
    source: 'appSettings',
  },
  {
    path: 'shoulderHoursEnd',
    label: 'Shoulder hours end',
    example: '6:00 PM',
    source: 'appSettings',
  },
  {
    path: 'workDays',
    label: 'Work days (compact)',
    example: 'Mon–Fri',
    source: 'appSettings',
  },
  {
    path: 'workDaysLong',
    label: 'Work days (long form)',
    example: 'Monday to Friday',
    source: 'appSettings',
  },
  {
    path: 'timezone',
    label: 'Timezone',
    example: 'Australia/Sydney',
    source: 'appSettings',
  },
  {
    path: 'returnGraceDays',
    label: 'Return grace period (days)',
    example: '14',
    source: 'appSettings',
  },

  // session
  {
    path: 'session.number',
    label: 'Check-in number',
    example: 'CI-2026-0184',
    source: 'session',
  },
  {
    path: 'session.intakeType',
    label: 'Intake type',
    example: 'In store',
    source: 'session',
  },
  {
    path: 'session.amountDue',
    label: 'Amount due',
    example: '$180.00',
    source: 'session',
    // Zero renders as "$0.00", never blank: a total is a fact, and a blank in
    // a terms sentence about money reads as an error.
    fallback: '$0.00',
  },
  {
    path: 'session.expiresAt',
    label: 'Check-in link expires',
    example: '19 June 2026',
    source: 'session',
  },
  {
    path: 'terms.version',
    label: 'Terms version',
    example: '2026-08-15',
    source: 'session',
  },

  // customer — unset until step two of the intake; see `fallback`.
  {
    path: 'customer.name',
    label: 'Customer name',
    example: 'Jane Smith',
    source: 'customer',
    fallback: 'you',
  },
  {
    path: 'customer.businessName',
    label: 'Customer business name',
    example: 'Acme Pty Ltd',
    source: 'customer',
  },
  {
    path: 'customer.email',
    label: 'Customer email',
    example: 'jane@acme.com.au',
    source: 'customer',
  },
  {
    path: 'customer.phone',
    label: 'Customer phone',
    example: '+61412345678',
    source: 'customer',
  },

  // system
  {
    path: 'system.companyName',
    label: 'Our company name',
    example: 'Tech Solvers',
    source: 'system',
  },
  {
    path: 'system.today',
    label: "Today's date",
    example: '19 May 2026',
    source: 'system',
  },
];
